import Api from "@/api/api";
import {CustomerContext, ModalContext} from "@/contexts";
import {Form} from "@/shared";
import {Modal} from "@/shared/Modal/Modal";
import {useContext, useState} from "react";
import "./EditClientModal.scss";
import {toast} from "react-toastify";
import {IClient} from "@/interfaces/interface";

type Props = {
  client: IClient;
  getClients: () => Promise<void>;
};

type FormValue = {
  firstName: string;
  lastName: string;
  name: string;
  contactInfo: string;
  billingInfo: string;
};

type FormErrorsType = {
  firstName?: string;
  lastName?: string;
  name?: string;
  contactInfo?: string;
  billingInfo?: string;
};

const initialState = {
  firstName: "",
  lastName: "",
  name: "",
  contactInfo: "",
  billingInfo: "",
};

export function EditClientModal({client, getClients}: Props) {
  const {closeModal} = useContext(ModalContext);
  const {getTeams} = useContext(CustomerContext);

  const [loading, setLoading] = useState(false);

  const [formValue, setFormValue] = useState<FormValue>({
    firstName: client?.admin?.userInfo?.firstName || '',
    lastName: client?.admin?.userInfo?.lastName || '',
    name: client.name,
    contactInfo: client.contactInfo || "",
    billingInfo: client.billingInfo || "",
  });

  const [formError, setFormError] = useState<FormErrorsType>({});

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = event.target;
    setFormValue((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  const {firstName, lastName, name, contactInfo, billingInfo} = formValue;

  const validate = () => {
    const errors: FormErrorsType = {};

    if (name?.length < 4) {
      errors.name = "Org Name should be longer than 3 symbols";
    }
    if (contactInfo?.length < 4) {
      errors.contactInfo = "Contact info should be longer than 3 symbols";
    }
    if (billingInfo?.length < 4) {
      errors.billingInfo = "Billing info should be longer than 3 symbols";
    }
    if (firstName?.length < 2) {
      errors.firstName = "First name should be longer than 1 symbols";
    }
    if (lastName?.length < 2) {
      errors.lastName = "Last name should be longer than 1 symbols";
    }
    if (Object.keys(errors).length) {
      return errors;
    }
  };

  const handleConfirm = async () => {
    const errors = validate();
    if (errors) {
      setFormError(errors);
      return;
    }
    setLoading(true);

    try {
      const res = await Api.put(`/v2/admin/organizations/${client.id}`, {
        firstName,
        lastName,
        name,
        contactInfo,
        billingInfo,
      });
      if (res.status === 200) {
        await getClients();
        closeModal();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Some error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal>
      <Modal.Header text="Edit Org Admin Info"/>

      <Form.Row>
        <Form.Label>Coach First Name</Form.Label>
        <Form.Input
            errorMessage={formError.firstName}
            onChange={handleChange}
            value={firstName}
            name={"firstName"}
            maxLength={40}
        />
      </Form.Row>

      <Form.Row>
        <Form.Label>Coach Last Name</Form.Label>
        <Form.Input
            errorMessage={formError.lastName}
            onChange={handleChange}
            value={lastName}
            name={"lastName"}
            maxLength={40}
        />
      </Form.Row>

      <Form.Row>
        <Form.Label>Org Name</Form.Label>
        <Form.Input
          errorMessage={formError.name}
          onChange={handleChange}
          value={name}
          name={"name"}
          maxLength={40}
        />
      </Form.Row>

      <Form.Row>
        <Form.Label>Contact info</Form.Label>
        <Form.Input
          errorMessage={formError.contactInfo}
          placeholder="ZIP, LA, Pedastrian street, +12064512559"
          onChange={handleChange}
          value={contactInfo}
          name={"contactInfo"}
          maxLength={50}
        />
      </Form.Row>

      <Form.Row>
        <Form.Label>Billing info</Form.Label>
        <Form.Input
          errorMessage={formError.billingInfo}
          placeholder="ZIP, LA, Pedastrian street, +12064512559"
          onChange={handleChange}
          value={billingInfo}
          name={"billingInfo"}
          maxLength={50}
        />
      </Form.Row>

      <Modal.Footer>
        <Form.Button type="outline" onClick={closeModal}>
          CANCEL
        </Form.Button>

        <Form.Button onClick={handleConfirm}>SAVE</Form.Button>
      </Modal.Footer>
    </Modal>
  );
}
